<template>
  <!-- PAGE TRANSITION -->
  <transition name="page-fade" appear>
    <div v-if="!pageLoading">

      <!-- HERO -->
      <PageHero
        :title="$t('hero.title_properties')"
        :breadcrumbsTop="[
          { label: $t('hero.breadcrumbs.home'), link: '/' },
          { label: $t('hero.breadcrumbs.properties') }
        ]"
        :backgroundImageUrl="heroBackground"
        :loaded="true"
      />

      <!-- CONTENT -->
      <div class="container py-10">

        <MiniFilterBar @update="onFilterUpdate" />

        <!-- CARDS -->
        <PropertyCards :properties="properties" />

        <!-- PAGINATION -->
        <PropertyPagination
          v-if="pagination.total > pagination.per_page"
          :current-page="currentPage"
          :total-pages="totalPages"
          @previous="previousPage"
          @next="nextPage"
        />
      </div>
    </div>
  </transition>

  <!-- SKELETON -->
  <div v-if="pageLoading" class="page-skeleton">
    <div class="skeleton hero-skeleton"></div>
    <div class="container mt-4">
      <div class="row">
        <div v-for="i in 6" :key="i" class="col-md-4 mb-4">
          <div class="skeleton card-skeleton"></div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useLanguageStore } from '@/stores/languageStore'
import { useFilterStore } from '@/stores/filterStore'

import MiniFilterBar from '../components/MiniFilterBar.vue'
import PropertyCards from '../pages/property_list/PropertyCards.vue'
import PropertyPagination from '../pages/property_list/PropertyPagination.vue'
import PageHero from '../components/PageHero.vue'

/* PAGE STATE */
const pageLoading = ref(true)

/* STORES */
const { locale } = storeToRefs(useLanguageStore())
const filterStore = useFilterStore()
const {
  catalog_id,
  country_id,
  region_id,
  city_id,
  district_id,
  category,
  type,
  subcategory,
  purpose
} = storeToRefs(filterStore)

/* HERO */
const heroBackground = ref(null)

/* DATA */
const properties = ref([])
const pagination = ref({ page: 1, per_page: 25, total: 0 })

/* COMPUTED */
const currentPage = computed(() => pagination.value.page)
const totalPages = computed(() =>
  Math.ceil(pagination.value.total / pagination.value.per_page)
)

/* LOAD HERO */
async function loadHeroBanner () {
  const res = await fetch(
    `/api/v1/banners?placement=properties`,
    {
      headers: { 'X-Locale': (locale.value || 'ru').split('-')[0] },
      cache: 'no-store'
    }
  )

  const banner = (await res.json())?.[0]
  if (!banner?.image_url) return

  const img = new Image()
  img.src = banner.image_url
  await img.decode()

  heroBackground.value = banner.image_url
}

/* LOAD PROPERTIES */
async function fetchProperties (filters = null) {
  const xLocale = (locale.value || 'ru').split('-')[0]

  const params = new URLSearchParams({
    catalog_id: filters?.catalog_id || catalog_id.value || '',
    country_id: filters?.country_id || country_id.value || '',
    region_id: filters?.region_id || region_id.value || '',
    city_id: filters?.city_id || city_id.value || '',
    district_id: filters?.district_id || district_id.value || '',
    main_category: filters?.main_category || category.value || '',
    property_type: filters?.property_type || type.value || '',
    property_subtype: filters?.property_subtype || subcategory.value || '',
    purpose: filters?.purpose || purpose.value || '',
    page: pagination.value.page,
    per_page: pagination.value.per_page,
    locale: xLocale
  })

  const res = await fetch(`/api/v1/properties?${params}`, {
    headers: { 'X-Locale': xLocale },
    cache: 'no-store'
  })

  const data = await res.json()
  properties.value = data.properties || []
  pagination.value.total = data.pagination?.total || 0
}

/* RELOAD PAGE */
async function reloadPage (filters = null) {
  pageLoading.value = true
  await Promise.all([
    loadHeroBanner(),
    fetchProperties(filters)
  ])
  pageLoading.value = false
}

/* FILTER UPDATE */
function onFilterUpdate (filters) {
  filterStore.setFilters(filters)
  pagination.value.page = 1
  reloadPage(filters)
}

/* PAGINATION */
function nextPage () {
  if (pagination.value.page < totalPages.value) {
    pagination.value.page++
    reloadPage()
  }
}

function previousPage () {
  if (pagination.value.page > 1) {
    pagination.value.page--
    reloadPage()
  }
}

/* INIT */
onMounted(() => {
  reloadPage()
  window.addEventListener('mini-filter-updated', e => {
    onFilterUpdate(e.detail)
  })
})

watch(locale, () => {
  pagination.value.page = 1
  reloadPage()
})

</script>

<style scoped>
/* PAGE TRANSITION */
.page-fade-enter-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.page-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.page-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* SKELETON */
.skeleton {
  background: linear-gradient(90deg, #eee, #f5f5f5, #eee);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
  border-radius: 12px;
}
.hero-skeleton {
  height: 516px;
}
.card-skeleton {
  height: 340px;
}
@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
}

</style>
